import api from '../lib/api';
import { apiUrl } from '../lib/api-url';

export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface AIChatResponse {
  content: string;
  provider: string;
  model: string;
  tokens: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number };
  cost: number;
  fallback: boolean;
  conversation_id: string;
}

export interface AIConversation {
  id: string;
  tenant_id: string;
  user_id: string;
  title: string;
  mode: string;
  provider_used: string;
  model_used: string;
  total_tokens: number;
  total_cost: number;
  message_count: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface AIGeneratedContent {
  id: string;
  content_type: string;
  output_content: any;
  provider_used: string;
  model_used: string;
  tokens_used: number;
  applied: boolean;
  created_at: string;
}

export interface AIConfig {
  id: string;
  tenant_id: string;
  provider: string;
  model_name: string | null;
  temperature: number;
  max_tokens: number;
  top_p: number;
  auto_execute: boolean;
  use_builtin_if_no_key: boolean;
  has_openai_key: boolean;
  has_anthropic_key: boolean;
  has_groq_key: boolean;
  has_together_key: boolean;
  has_openrouter_key: boolean;
  custom_system_prompt: string | null;
  features: Record<string, boolean>;
}

export interface PromptTemplate {
  id: string;
  name: string;
  category: string;
  template: string;
  variables: { name: string; type: string; default: string; options?: string[] }[];
  description: string;
  is_system: boolean;
}

export async function chat(
  messages: ChatMessage[],
  stream = false,
  taskType = 'chat'
): Promise<AIChatResponse> {
  if (stream) {
    throw new Error('Use chatStream for streaming');
  }
  const response = await api.post('/ai/chat', { messages, stream: false, task_type: taskType });
  return response.data.data;
}

export async function* chatStream(
  messages: ChatMessage[],
  taskType = 'chat',
  signal?: AbortSignal
): AsyncGenerator<string, AIChatResponse, void> {
  const token = localStorage.getItem('access_token');
  const response = await fetch(apiUrl('/api/v1/ai/chat'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ messages, stream: true, task_type: taskType }),
    signal,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Stream failed' }));
    throw new Error(error.message || error.error || 'Stream failed');
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('ReadableStream not supported');

  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (data.chunk) {
            fullContent += data.chunk;
            yield data.chunk;
          }
          if (data.done) {
            return {
              content: fullContent,
              provider: 'vsends',
              model: '',
              tokens: {},
              cost: 0,
              fallback: false,
              conversation_id: '',
            };
          }
        } catch {
          // skip malformed SSE lines
        }
      }
    }
  }

  return {
    content: fullContent,
    provider: 'vsends',
    model: '',
    tokens: {},
    cost: 0,
    fallback: false,
    conversation_id: '',
  };
}

export async function generate(
  contentType: string,
  prompt: string,
  context?: Record<string, any>
): Promise<AIGeneratedContent & { output: any; conversation_id: string }> {
  const response = await api.post('/ai/generate', {
    content_type: contentType,
    prompt,
    context,
  });
  return response.data.data;
}

export async function act(
  action: string,
  params: Record<string, any>,
  confirm = false
): Promise<{ type: string; action: string; data: any; message: string; description?: string; requires_confirmation?: boolean }> {
  const response = await api.post('/ai/act', { action, params, confirm });
  return response.data.data;
}

export async function getConversations(page = 1, perPage = 20, status = 'active'): Promise<{ items: AIConversation[]; pagination: any }> {
  const response = await api.get('/ai/conversations', { params: { page, per_page: perPage, status } });
  return response.data.data;
}

export async function getConversation(id: string): Promise<AIConversation> {
  const response = await api.get(`/ai/conversations/${id}`);
  return response.data.data;
}

export async function deleteConversation(id: string): Promise<void> {
  await api.delete(`/ai/conversations/${id}`);
}

export async function getConfig(): Promise<AIConfig> {
  const response = await api.get('/ai/config');
  return response.data.data;
}

export async function updateConfig(data: Partial<AIConfig>): Promise<AIConfig> {
  const response = await api.put('/ai/config', data);
  return response.data.data;
}

export async function getTemplates(category?: string): Promise<{ items: PromptTemplate[] }> {
  const response = await api.get('/ai/templates', { params: category ? { category } : {} });
  return response.data.data;
}

export async function createTemplate(data: {
  name: string;
  category: string;
  template: string;
  variables?: any[];
  description?: string;
  system_prompt?: string;
}): Promise<PromptTemplate> {
  const response = await api.post('/ai/templates', data);
  return response.data.data;
}
